import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { roomFooterTextHandler } from '../../utils/room-footer-text-handler';

import { useGetHistoryMoves } from 'modules/Room/Hooks/useGetHistoryMoves';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { RoomFooterText } from 'shared/components/ui/room-footer-text/room-footer-text';
import { MovesEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { getMoveIcon } from 'shared/utils/getMoveIcon';
import { getMoveIconText } from 'shared/utils/getMoveIconText';

import emblem from 'assets/png/emblem.png?format=webp;avif&as=picture';

import { MobileHistoryMove } from './history-move';

export const MobileHistoryMoves = ({ move }: { move: MovesEnum }) => {
  const gameHistory = useGetHistoryMoves();
  const historyRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);
  const { activeRoomId } = useParams();
  const { chain } = useAccount();

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully rendered before measuring
    const checkScrollable = () => {
      if (historyRef.current) {
        // Calculate total width of all children
        const children = Array.from(historyRef.current.children);
        let totalChildrenWidth = 0;

        children.forEach((child) => {
          // Include the child's width plus any margins
          const childStyles = window.getComputedStyle(child);
          const marginLeft = parseInt(childStyles.marginLeft || '0', 10);
          const marginRight = parseInt(childStyles.marginRight || '0', 10);
          totalChildrenWidth += child.getBoundingClientRect().width + marginLeft + marginRight;
        });

        // Add gap width between items (1.5rem = 24px * (children.length-1))
        const gapWidth = children.length > 1 ? (children.length - 1) * 6 : 0;
        totalChildrenWidth += gapWidth;

        const containerWidth = historyRef.current.clientWidth;

        setIsScrollable(totalChildrenWidth > containerWidth);
      }
    };

    // Wait for next frame to ensure rendering is complete
    requestAnimationFrame(checkScrollable);
  }, [gameHistory]);

  // Effect to scroll to show as many items as possible
  useEffect(() => {
    if (historyRef.current && isScrollable) {
      const scrollAmount = Math.max(
        0,
        historyRef.current.scrollWidth - historyRef.current.clientWidth,
      );
      historyRef.current.scrollLeft = scrollAmount * 2;
    }
  }, [gameHistory.length, isScrollable]);

  if (!activeRoomId) return null;

  return (
    <div className="flex w-full flex-col justify-center">
      <div className="flex items-center">
        <div
          ref={historyRef}
          className={`no-scrollbar flex h-full w-full items-center gap-1.5 overflow-x-scroll whitespace-nowrap pr-2 ${!isScrollable ? 'justify-end' : ''} `}
        >
          {gameHistory.length > 1 ? (
            [...gameHistory]
              .reverse()
              .map((round, index) => (
                <MobileHistoryMove key={round.encrMoveA + index} round={round} />
              ))
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <p className="pl-20 text-center text-md text-white/50 sm:text-base">
                Play one round to see history
              </p>
            </div>
          )}
        </div>
        <div className="mr-2 h-[54px] w-[1px] bg-border sm:h-[108px]" />
        <div className="mr-2 flex h-[54px] items-center justify-end sm:h-[108px]">
          {move ? (
            <div
              className="rounded-lg p-[1px] sm:rounded-xl"
              style={{
                background: 'linear-gradient(180deg, #FFFFFF 0%, #FFA700 100%)',
              }}
            >
              <div
                className="relative flex h-[52px] w-10 shrink-0 flex-col justify-between overflow-hidden rounded-[8px] sm:h-[108px] sm:w-[80px] sm:rounded-xl"
                style={{
                  background: 'linear-gradient(180deg, #0D1450 0%, #FFC000 158.75%)',
                }}
              >
                <div className="flex h-full w-full items-center justify-center">
                  {getMoveIcon(move, 'w-[26px] sm:w-12')}
                </div>
                <div
                  className="flex h-3 shrink-0 items-center justify-center sm:h-6"
                  style={{
                    background: 'linear-gradient(292.04deg, #FF8B00 14.41%, #FFC000 85.59%)',
                  }}
                >
                  <div className="text-left text-[8px] font-semibold leading-[8px] text-white sm:text-base">
                    {getMoveIconText(move)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="rounded-[8px] p-[1px] sm:rounded-xl"
              style={{
                background:
                  'linear-gradient(180deg, rgba(50, 46, 102, 1) 0%, rgba(55, 52, 112, 1) 100%)',
              }}
            >
              <div
                className="relative flex h-[54px] w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg sm:h-[108px] sm:w-20 sm:rounded-xl"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(21, 23, 40, 1) 0%, rgba(29, 33, 62, 1) 100%)',
                }}
              >
                <OptimizedImage
                  alt="emblem"
                  className="h-[30px] w-[30px] sm:h-10 sm:w-10"
                  style={{ objectFit: 'cover' }}
                  imageData={emblem}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <RoomFooterText children={<>{roomFooterTextHandler({ chain, activeRoomId })}</>} />
    </div>
  );
};
